import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Autocomplete, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid,
    InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import EnergyAdminApiService from '../../../api/EnergyAdminApiService';
import { getCountryOptions, getTimeZoneOptions, STATE_PROVINCE_OPTIONS } from '../../../constants/geographyConstants';
import SaveButton from '../../Common/SaveButton';
import { useSnackbar } from '../../Common/SnackbarProvider';
import { useAsyncCallback } from '../../../hooks/useAsyncCallback';

const energyAdminApiService = new EnergyAdminApiService();

const EMPTY = {
    code: '',
    name: '',
    addressLine: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: 'US',
    latitude: '',
    longitude: '',
    weatherStationId: '',
    timeZoneId: '',
    heatingBaseTempF: '',
    coolingBaseTempF: '',
    sortSeq: '',
    isActive: true,
};

function toForm (site) {
    if (!site) return { ...EMPTY };

    return {
        code: site.code ?? '',
        name: site.name ?? '',
        addressLine: site.addressLine ?? '',
        city: site.city ?? '',
        stateProvince: site.stateProvince ?? '',
        postalCode: site.postalCode ?? '',
        country: site.country ?? '',
        latitude: site.latitude ?? '',
        longitude: site.longitude ?? '',
        weatherStationId: site.weatherStationId ?? '',
        timeZoneId: site.timeZoneId ?? '',
        heatingBaseTempF: site.heatingBaseTempF ?? '',
        coolingBaseTempF: site.coolingBaseTempF ?? '',
        sortSeq: site.sortSeq ?? '',
        isActive: site.isActive ?? true,
    };
}

function numberOrNull (value) {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Creates or edits an energy site: a physical facility inside a location. Address or coordinates
 * matter more than they look — without either, weather cannot be fetched and no weather-normalized
 * analysis can run for the site's meters.
 */
export default function EditEnergySiteModal ({ open, projectId, currentSite, onCancel, onSave }) {
    const { t, i18n } = useTranslation();
    const snackbar = useSnackbar();

    const [form, setForm] = useState({ ...EMPTY });

    useEffect(() => {
        if (open) {
            setForm(toForm(currentSite));
        }
    }, [open, currentSite]);

    const update = (field, value) => setForm(current => ({ ...current, [field]: value }));

    // A state from the old country would mislead the geocoder, so changing country clears it.
    const updateCountry = country => setForm(current => (
        country === current.country ? current : { ...current, country, stateProvince: '' }
    ));

    const countryOptions = useMemo(() => getCountryOptions(i18n.language), [i18n.language]);
    const timeZoneOptions = useMemo(() => getTimeZoneOptions(), []);

    // A saved value outside a list (typed before these were dropdowns) stays selectable rather
    // than silently blanking out.
    const withCurrent = (options, current) => (
        options && current && !options.some(option => option.value === current)
            ? [{ value: current, label: current }, ...options]
            : options
    );
    const countryOptionsWithCurrent = withCurrent(countryOptions, form.country);
    const stateOptionsWithCurrent = withCurrent(STATE_PROVINCE_OPTIONS[form.country], form.stateProvince);
    const timeZoneOptionsWithCurrent = withCurrent(timeZoneOptions, form.timeZoneId);

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        if (!form.code.trim()) {
            snackbar.error(t('energySetup.validation.codeRequired'));
            return;
        }

        const payload = {
            code: form.code.trim(),
            name: form.name || null,
            addressLine: form.addressLine || null,
            city: form.city || null,
            stateProvince: form.stateProvince || null,
            postalCode: form.postalCode || null,
            country: form.country || null,
            latitude: numberOrNull(form.latitude),
            longitude: numberOrNull(form.longitude),
            weatherStationId: form.weatherStationId || null,
            timeZoneId: form.timeZoneId || null,
            heatingBaseTempF: numberOrNull(form.heatingBaseTempF),
            coolingBaseTempF: numberOrNull(form.coolingBaseTempF),
            sortSeq: numberOrNull(form.sortSeq),
            isActive: form.isActive,
        };

        const response = currentSite
            ? await energyAdminApiService.updateSite(currentSite.id, payload)
            : await energyAdminApiService.createSite(projectId, payload);

        if (!response?.success) {
            snackbar.error(response?.message ?? t('energySetup.toast.saveFailed'));
            return;
        }

        snackbar.success(currentSite
            ? t('energySetup.toast.siteUpdated', { code: payload.code })
            : t('energySetup.toast.siteCreated', { code: payload.code }));

        onSave();
    });

    return (
        <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
            <DialogTitle>
                {currentSite
                    ? t('energySetup.site.editTitle', { code: currentSite.code })
                    : t('energySetup.site.createTitle')}
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            required
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 40 }}
                            label={t('energySetup.field.code')}
                            helperText={t('energySetup.help.code')}
                            value={form.code}
                            onChange={event => update('code', event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 120 }}
                            label={t('energySetup.field.name')}
                            value={form.name}
                            onChange={event => update('name', event.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary">{t('energySetup.section.whereItIs')}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('energySetup.help.weather')}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 200 }}
                            label={t('energySetup.field.addressLine')}
                            value={form.addressLine}
                            onChange={event => update('addressLine', event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 120 }}
                            label={t('energySetup.field.city')}
                            value={form.city}
                            onChange={event => update('city', event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        {stateOptionsWithCurrent ? (
                            <FormControl fullWidth size="small">
                                <InputLabel id="site-state">{t('energySetup.field.stateProvince')}</InputLabel>
                                <Select
                                    labelId="site-state"
                                    label={t('energySetup.field.stateProvince')}
                                    value={form.stateProvince}
                                    onChange={event => update('stateProvince', event.target.value)}
                                >
                                    <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                                    {stateOptionsWithCurrent.map(option => (
                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <TextField
                                fullWidth
                                size="small"
                                inputProps={{ maxLength: 80 }}
                                label={t('energySetup.field.stateProvince')}
                                value={form.stateProvince}
                                onChange={event => update('stateProvince', event.target.value)}
                            />
                        )}
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 20 }}
                            label={t('energySetup.field.postalCode')}
                            value={form.postalCode}
                            onChange={event => update('postalCode', event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="site-country">{t('energySetup.field.country')}</InputLabel>
                            <Select
                                labelId="site-country"
                                label={t('energySetup.field.country')}
                                value={form.country}
                                onChange={event => updateCountry(event.target.value)}
                            >
                                <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                                {countryOptionsWithCurrent.map(option => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            inputProps={{ inputMode: 'decimal' }}
                            label={t('energySetup.field.latitude')}
                            value={form.latitude}
                            onChange={event => update('latitude', event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            inputProps={{ inputMode: 'decimal' }}
                            label={t('energySetup.field.longitude')}
                            value={form.longitude}
                            onChange={event => update('longitude', event.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary">{t('energySetup.section.analysis')}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            inputProps={{ inputMode: 'decimal' }}
                            label={t('energySetup.field.heatingBaseTemp')}
                            helperText={t('energySetup.help.baseTemp')}
                            value={form.heatingBaseTempF}
                            onChange={event => update('heatingBaseTempF', event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            inputProps={{ inputMode: 'decimal' }}
                            label={t('energySetup.field.coolingBaseTemp')}
                            value={form.coolingBaseTempF}
                            onChange={event => update('coolingBaseTempF', event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={timeZoneOptionsWithCurrent}
                            value={timeZoneOptionsWithCurrent.find(option => option.value === form.timeZoneId) ?? null}
                            onChange={(event, option) => update('timeZoneId', option?.value ?? '')}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={params => (
                                <TextField {...params} label={t('energySetup.field.timeZoneId')} placeholder="America/Chicago" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            inputProps={{ inputMode: 'numeric' }}
                            label={t('energySetup.field.sortSeq')}
                            value={form.sortSeq}
                            onChange={event => update('sortSeq', event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={form.isActive}
                                    onChange={event => update('isActive', event.target.checked)}
                                />
                            )}
                            label={t('common.active')}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={saving}>{t('common.cancel')}</Button>
                <SaveButton variant="contained" loading={saving} onClick={handleSave}>{t('common.save')}</SaveButton>
            </DialogActions>
        </Dialog>
    );
}

EditEnergySiteModal.propTypes = {
    open: PropTypes.bool.isRequired,
    projectId: PropTypes.number,
    currentSite: PropTypes.object,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
};
